import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { escalateTicket, getTicketEscalations } from "controller/api";
import type {
  ApiErrorType,
  EscalateTicketData,
  EscalateTicketInput,
  EscalationHistoryItem,
} from "shared";
import { sentryLogger } from "utilz";

// GET /servicedesk/ticket/{ticketId}/escalations  — full history, newest first.
// Query key: ["ticket_escalations", ticketId]  (LOCKED — see IMPLEMENTATION_PLAN.md §1)
// Disabled when ticketId is falsy.
export const useGetTicketEscalations = (ticketId?: string) => {
  return useQuery<EscalationHistoryItem[], ApiErrorType>({
    queryKey: ["ticket_escalations", ticketId],
    queryFn: () => getTicketEscalations(ticketId ?? ""),
    enabled: Boolean(ticketId),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "ticket",
        functionName: "getTicketEscalations",
      }),
  });
};

// POST /servicedesk/ticket/{ticketId}/escalate/
// On success — DUAL invalidation (Bastian API_CONTRACT.md §4):
//   1. ["ticket", ticketId]            → refetches ticket-by-id (picks up updated `escalatedMembers`,
//                                         `ticket_status`, `status_name`, `ticket_owner*`, `schedules[]`,
//                                         `scheduled_hours`).
//   2. ["ticket_escalations", ticketId] → refetches the multi-cycle history (timeline / popover surfaces).
//
// Toasts intentionally NOT fired here — UI/component layer owns user feedback
// (project memory rule: feedback_toast_placement.md).
export const useEscalateTicket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ticketId,
      payload,
    }: {
      ticketId: string;
      payload: EscalateTicketInput;
    }): Promise<EscalateTicketData> => escalateTicket(ticketId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["ticket", variables.ticketId]);
      queryClient.invalidateQueries(["ticket_escalations", variables.ticketId]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error,
        workflow: "ticket",
        functionName: "escalateTicket",
      }),
  });
};
